/**
 * The organization's refund switch (GH-951, DEC-058; owner 2026-10-17), and who approves a refund
 * (GH-952, DEC-059; owner 2026-10-18).
 *
 * "Add a switch to turn [refunds] off for orgs that prefer." Default on — refunds allowed, the
 * behaviour every organization had before.
 *
 * When off, the API refuses — with `refunds_disabled` (409) and a staff sentence — a console gift
 * refund, a ticket-order refund, creating a held refund, and approving one already held (the
 * approval re-checks at approval time). It never refuses a cancel of an uncaptured end-of-day
 * authorization (DEC-056: nothing was charged, no money moves), and it never stops the books
 * recording a refund the processor made on its own or one made in its dashboard: that money has
 * already left, so `charge.refunded` is still posted. Disputes are the card issuer's and are
 * untouched.
 *
 * **Who approves a refund (GH-952).** The owner removed the processor's own approval rule and moved
 * the second approval here. `every` (the default for every organization): a second person approves
 * every refund. `above_amount`: only refunds above the organization's amount (strictly above — a
 * refund of exactly the amount runs at once). `platform_limits`: only what the platform limits hold
 * (above $500, or more than $2,500 refunded in a rolling day — the behaviour before GH-952). The
 * platform limits are a ceiling under every mode. The person who asked for a refund can never
 * approve it.
 */
import { DEFAULT_REFUND_LIMITS, MAX_REFUND_APPROVAL_THRESHOLD_MINOR, REFUND_APPROVAL_MODES, } from "@givehere/domain";
import { z } from "zod";
import { minorUnitsSchema, moneySchema } from "./v1.js";
/** What the console shows wherever a refund would have been offered while refunds are off. */
export const REFUNDS_DISABLED_MESSAGE = "Refunds are turned off for this organization. An owner or admin can turn them back on in Org info.";
/**
 * GH-952: said with a held refund when nobody else in the organization holds the permission to
 * approve it — the requester can never approve their own, so it would wait forever.
 */
export const NO_OTHER_REFUND_APPROVER_MESSAGE = "No one else in your organization can approve refunds. Add an owner, admin, or finance staff member in Team, or change who approves refunds in Org info.";
/**
 * GH-953: said with a held refund when its requester is an owner or admin and nobody else in the
 * organization can approve refunds — so they may approve their own, with the usual step-up.
 */
export const SELF_APPROVAL_MESSAGE = "You're the only person who can approve refunds here, so you can approve your own.";
export { REFUND_APPROVAL_MODES };
/**
 * DEC-006's working assumption, for a console reading a response from before GH-952 (which carried
 * no `platformLimits`). The API's figure wins whenever it is there.
 */
export const DEFAULT_PLATFORM_REFUND_LIMITS_WIRE = {
    singleRefund: {
        amountMinor: DEFAULT_REFUND_LIMITS.singleRefundMinor.toString(10),
        currency: "USD",
    },
    perOrganizationPerDay: {
        amountMinor: DEFAULT_REFUND_LIMITS.perOrganizationPerDayMinor.toString(10),
        currency: "USD",
    },
};
/** Whole dollars, in minor units: > 0, at most $100,000. */
export const refundApprovalThresholdMinorSchema = minorUnitsSchema.refine((value) => {
    const minor = BigInt(value);
    return minor > 0n && minor <= MAX_REFUND_APPROVAL_THRESHOLD_MINOR && minor % 100n === 0n;
}, { message: "The amount must be a whole number of dollars, from $1 to $100,000." });
/**
 * `GET|PUT /v1/organizations/:organizationId/refund-settings` — owner and admin
 * (`organization.update_profile`); anyone else reads 403, another organization 404. A save that
 * changes nothing writes nothing.
 *
 * The PUT body names what changes — any of the three fields, at least one. A change to the switch
 * is audited as `organization.refunds_enabled.updated`; a change to who approves as
 * `organization.refund_approval.updated` (before/after). `refundApprovalThresholdMinor` is
 * required exactly when the mode is `above_amount`, and is sent only with the mode.
 *
 * Loosening who approves (`every` → `above_amount` or `platform_limits`, `above_amount` →
 * `platform_limits`, or a higher amount) needs a fresh step-up: without one the answer is the
 * usual `step_up_required`, and the console's "confirm it's you" prompt retries it. Tightening
 * does not.
 */
export const refundSettingsRequestSchema = z
    .object({
    refundsEnabled: z.boolean().optional(),
    refundApprovalMode: z.enum(REFUND_APPROVAL_MODES).optional(),
    refundApprovalThresholdMinor: refundApprovalThresholdMinorSchema.nullable().optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.refundsEnabled === undefined &&
        value.refundApprovalMode === undefined &&
        value.refundApprovalThresholdMinor === undefined) {
        context.addIssue({ code: "custom", message: "Nothing to change." });
        return;
    }
    const threshold = value.refundApprovalThresholdMinor ?? null;
    if (value.refundApprovalMode === undefined) {
        if (value.refundApprovalThresholdMinor !== undefined) {
            context.addIssue({
                code: "custom",
                message: "Send the amount with refundApprovalMode.",
                path: ["refundApprovalMode"],
            });
        }
        return;
    }
    if (value.refundApprovalMode === "above_amount" && threshold === null) {
        context.addIssue({
            code: "custom",
            message: "Choose the amount above which a second person approves.",
            path: ["refundApprovalThresholdMinor"],
        });
    }
    if (value.refundApprovalMode !== "above_amount" && threshold !== null) {
        context.addIssue({
            code: "custom",
            message: "An amount applies only to above_amount.",
            path: ["refundApprovalThresholdMinor"],
        });
    }
});
export const refundSettingsResponseSchema = z.object({
    refundsEnabled: z.boolean(),
    /** GH-952. Defaults keep a response from before it readable. */
    refundApprovalMode: z.enum(REFUND_APPROVAL_MODES).default("every"),
    refundApprovalThresholdMinor: minorUnitsSchema.nullable().default(null),
    /** The platform limits in force — the ceiling every mode keeps — so the console can say them. */
    platformLimits: z
        .object({ singleRefund: moneySchema, perOrganizationPerDay: moneySchema })
        .nullable()
        .default(null),
});
//# sourceMappingURL=refund-settings.js.map